export type SurveyTrack = 'retail' | 'cultivation' | 'hq';
export type QuestionType = 'scale' | 'text';
export type ScaleKey = 'agree' | 'rate' | 'manage' | 'comfort' | 'satisfy' | 'confidence' | 'connect';

export interface SurveyQuestion {
  id: string;
  text: string;
  type: QuestionType;
  cv?: string;
  scaleKey?: ScaleKey;
}

export interface SurveySection {
  id: string;
  title: string;
  icon: string;
  tracks: SurveyTrack[];
  questions: SurveyQuestion[];
}

export const ALL: Record<string, SurveySection> = {
  retail_specific: {
    id: 'retail_specific',
    title: 'Dispensary Experience',
    icon: '🏪',
    tracks: ['retail'],
    questions: [
      { id: 'rt1', text: 'How would you rate your overall work experience at the dispensary this month?', type: 'scale', scaleKey: 'rate' },
      { id: 'rt1a', text: 'If you rated below a 4, what could we do better?', type: 'text' },
      { id: 'rt2', text: 'How manageable does your workload feel on a typical day on the floor?', type: 'scale', scaleKey: 'manage' },
      { id: 'rt3', text: 'Do you have the tools you need to serve customers well (POS, product info, compliance materials)?', type: 'scale' },
      { id: 'rt3a', text: "If not, what's missing? We want to fix it.", type: 'text' },
      { id: 'rt4', text: 'How would you rate the overall working conditions at your location?', type: 'scale', scaleKey: 'rate' },
      { id: 'rt5', text: 'I feel empowered to resolve customer issues and deliver a great experience.', type: 'scale' },
      { id: 'rt6', text: 'I feel confident in my product knowledge and ability to make informed recommendations.', type: 'scale' },
      { id: 'rt7', text: 'I feel well-trained on the compliance and regulations specific to my state.', type: 'scale' },
    ],
  },
  cultivation_specific: {
    id: 'cultivation_specific',
    title: 'Cultivation & Processing',
    icon: '🌱',
    tracks: ['cultivation'],
    questions: [
      { id: 'cv1', text: 'How would you rate your overall work experience at the grow or processing facility this month?', type: 'scale', scaleKey: 'rate' },
      { id: 'cv1a', text: 'If you rated below a 4, what could we improve?', type: 'text' },
      { id: 'cv2', text: 'I have the equipment, tools, and supplies I need to do my job safely and effectively.', type: 'scale' },
      { id: 'cv3', text: 'How would you rate the safety conditions at your facility (ventilation, PPE, ergonomics)?', type: 'scale', scaleKey: 'rate' },
      { id: 'cv4', text: 'Quality standards and SOPs are clear and consistently followed on my team.', type: 'scale' },
      { id: 'cv5', text: 'The physical demands of my role are manageable, and I feel supported in staying healthy.', type: 'scale' },
      { id: 'cv6', text: 'I feel my work directly contributes to the quality of the product that reaches customers.', type: 'scale', cv: 'Team Mentality' },
      { id: 'cv7', text: 'Staffing levels during harvest and peak production feel adequate.', type: 'scale' },
      { id: 'cv8', text: 'I feel well-trained on safety protocols and state-specific cultivation compliance.', type: 'scale' },
    ],
  },
  leadership: {
    id: 'leadership',
    title: 'Leadership',
    icon: '🏔️',
    tracks: ['hq'],
    questions: [
      { id: 'l1', text: "Leadership does a good job communicating the company's vision and where we're headed.", type: 'scale' },
      { id: 'l2', text: 'I feel kept in the loop on important company updates and new market developments.', type: 'scale' },
      { id: 'l3', text: 'Leaders at Sweetspot are accessible — I could reach out if I needed to.', type: 'scale' },
      { id: 'l4', text: 'I trust leadership to navigate the challenges of a growing cannabis company.', type: 'scale', scaleKey: 'confidence' },
      { id: 'l5', text: 'Leadership fosters an environment where different backgrounds and perspectives are valued.', type: 'scale', cv: 'Creativity from Diversity' },
    ],
  },
  brand: {
    id: 'brand',
    title: 'Brand & Industry',
    icon: '🌿',
    tracks: ['hq'],
    questions: [
      { id: 'br1', text: "I see Sweetspot's core values show up in how we actually operate day to day.", type: 'scale', cv: 'Do Good' },
      { id: 'br2', text: 'I feel equipped to represent the Sweetspot brand and our mission in cannabis.', type: 'scale' },
      { id: 'br4', text: 'Sweetspot does a good job creating engagement opportunities at the corporate level (all-hands, offsites, team activities, etc.).', type: 'scale' },
    ],
  },
  engagement: {
    id: 'engagement',
    title: 'Company Engagement',
    icon: '🎯',
    tracks: ['retail', 'cultivation', 'hq'],
    questions: [
      { id: 'en1', text: 'Sweetspot does a good job creating team engagement opportunities (events, team activities, shoutouts, etc.).', type: 'scale' },
      { id: 'en2', text: 'I feel connected to the bigger Sweetspot mission, even from my day-to-day role.', type: 'scale', scaleKey: 'connect' },
      { id: 'en3', text: "I'm confident Sweetspot will continue to grow and succeed as the cannabis industry matures.", type: 'scale', scaleKey: 'confidence' },
      { id: 'en4', text: "I'm proud to work in cannabis and feel Sweetspot is building something meaningful.", type: 'scale' },
    ],
  },
  remote: {
    id: 'remote',
    title: 'Remote Work & Connectivity',
    icon: '🏠',
    tracks: ['hq'],
    questions: [
      { id: 'rm1', text: "Working remotely, I feel connected to what's happening across Sweetspot's locations.", type: 'scale', scaleKey: 'connect' },
      { id: 'rm2', text: 'I have enough face time (virtual or in-person) with the field teams to understand their day-to-day reality.', type: 'scale' },
      { id: 'rm3', text: 'Our remote communication tools and processes work well for how our team operates.', type: 'scale' },
      { id: 'rm4', text: "I feel included in decisions and conversations even when I'm not physically at a location.", type: 'scale', cv: 'Speak Up' },
      { id: 'rm5', text: 'The balance between remote flexibility and in-person collaboration feels right.', type: 'scale' },
      { id: 'rm7', text: 'I have the right setup and support from Sweetspot to be productive working from home.', type: 'scale' },
      { id: 'rm6', text: "What's one thing we could do to help remote and field teams feel more connected?", type: 'text' },
    ],
  },
  management: {
    id: 'management',
    title: 'Management',
    icon: '🧭',
    tracks: ['retail', 'cultivation', 'hq'],
    questions: [
      { id: 'mg1', text: 'My manager communicates clearly and leads in a way that works for our team.', type: 'scale' },
      { id: 'mg2', text: "I've gotten feedback from my manager that's actually helped me grow.", type: 'scale', cv: 'Growth Is a Process' },
      { id: 'mg3', text: "I'm comfortable bringing up concerns or tough topics with my manager.", type: 'scale', cv: 'Speak Up' },
      { id: 'mg4', text: 'My manager values my input and on-the-ground experience.', type: 'scale' },
      { id: 'mg5', text: "My manager has a good understanding of my skills and what I'm capable of.", type: 'scale' },
    ],
  },
  teamwork: {
    id: 'teamwork',
    title: 'Teamwork',
    icon: '🤝',
    tracks: ['retail', 'cultivation', 'hq'],
    questions: [
      { id: 'tw1', text: 'My team works well together to get things done.', type: 'scale', cv: 'Team Mentality' },
      { id: 'tw2', text: 'I feel comfortable sharing ideas and feedback openly with my team.', type: 'scale', cv: 'Speak Up' },
      { id: 'tw3', text: 'Communication on my team is solid — everyone knows what\'s going on.', type: 'scale' },
      { id: 'tw4', text: 'The workload on my team feels fairly shared.', type: 'scale' },
    ],
  },
  compensation: {
    id: 'compensation',
    title: 'Compensation & Benefits',
    icon: '💰',
    tracks: ['retail', 'cultivation', 'hq'],
    questions: [
      { id: 'cp1', text: 'I feel my pay is fair for the work I do and the responsibilities I carry.', type: 'scale' },
      { id: 'cp2', text: 'I have a clear understanding of how pay decisions and raises are made at Sweetspot.', type: 'scale' },
      { id: 'cp3', text: "Compared to similar roles in cannabis, I feel good about Sweetspot's overall compensation.", type: 'scale' },
      { id: 'cp4', text: "I'm satisfied with the benefits Sweetspot offers (medical, PTO, etc.).", type: 'scale', scaleKey: 'satisfy' },
      { id: 'cp5', text: 'I understand what benefits are available to me and how to use them.', type: 'scale' },
    ],
  },
  balance: {
    id: 'balance',
    title: 'Work-Life Balance',
    icon: '⚖️',
    tracks: ['retail', 'cultivation', 'hq'],
    questions: [
      { id: 'wl1', text: 'My schedule allows me enough time for life outside of work.', type: 'scale' },
      { id: 'wl2', text: "I'm satisfied with the PTO and sick time Sweetspot provides.", type: 'scale', scaleKey: 'satisfy' },
      { id: 'wl3', text: "My managers respect my time when I'm off the clock.", type: 'scale' },
      { id: 'wl4', text: 'I feel supported when I need to take time off for personal or mental health reasons.', type: 'scale' },
    ],
  },
  values: {
    id: 'values',
    title: 'Core Values & Cannabis Culture',
    icon: '💚',
    tracks: ['retail', 'cultivation', 'hq'],
    questions: [
      { id: 'v1', text: 'I see people at Sweetspot actually "speaking up" — sharing ideas and pushing us to improve.', type: 'scale', cv: 'Speak Up' },
      { id: 'v2', text: 'I feel encouraged to find smarter, better ways to do my job.', type: 'scale', cv: 'Speak Up' },
      { id: 'v3', text: 'When mistakes happen, we treat them as learning opportunities — not blame games.', type: 'scale', cv: 'Growth Is a Process' },
      { id: 'v4', text: 'I feel safe to try new things and learn from the experience.', type: 'scale', cv: 'Growth Is a Process' },
      { id: 'v5', text: 'Sweetspot genuinely walks the talk on sustainability, community, and being a responsible cannabis operator.', type: 'scale', cv: 'Do Good' },
      { id: 'v6', text: "I'm proud of Sweetspot's role in destigmatizing cannabis and serving our communities.", type: 'scale', cv: 'Do Good' },
      { id: 'v7', text: 'Different perspectives and fresh ideas are actually welcomed here — not just tolerated.', type: 'scale', cv: 'Creativity from Diversity' },
      { id: 'v8', text: 'I see real teamwork, pride in our craft, and attention to detail — from seed to sale.', type: 'scale', cv: 'Team Mentality' },
      { id: 'v9', text: "I'm proud to work at Sweetspot and would tell a friend to apply here.", type: 'scale' },
      { id: 'v10', text: 'Which core value do you think we live best — and which one needs the most work?', type: 'text' },
    ],
  },
};

export const TRACK_ORDER: Record<SurveyTrack, string[]> = {
  retail: ['retail_specific', 'engagement', 'management', 'teamwork', 'compensation', 'balance', 'values'],
  cultivation: ['cultivation_specific', 'engagement', 'management', 'teamwork', 'compensation', 'balance', 'values'],
  hq: ['leadership', 'brand', 'engagement', 'remote', 'management', 'teamwork', 'compensation', 'balance', 'values'],
};

export const SCALES: Record<ScaleKey, [string, string, string, string, string]> = {
  agree: ['Strongly disagree', 'Disagree', 'Neutral', 'Agree', 'Strongly agree'],
  rate: ['Poor', 'Below average', 'Average', 'Good', 'Excellent'],
  manage: ['Unmanageable', 'Difficult', 'Moderate', 'Manageable', 'Very manageable'],
  comfort: ['Not comfortable', 'Slightly', 'Somewhat', 'Comfortable', 'Very comfortable'],
  satisfy: ['Very dissatisfied', 'Dissatisfied', 'Neutral', 'Satisfied', 'Very satisfied'],
  confidence: ['Not confident', 'Slightly', 'Somewhat', 'Confident', 'Very confident'],
  connect: ['Not connected', 'Slightly', 'Somewhat', 'Connected', 'Very connected'],
};

export const getScaleKey = (q: SurveyQuestion): ScaleKey => q.scaleKey ?? 'agree';

export const TRACK_LABELS: Record<SurveyTrack, string> = {
  retail: 'Retail',
  cultivation: 'Cultivation',
  hq: 'Sweetspot Brands (Corporate)',
};

export const HQ_DEPARTMENTS = [
  'Operations — General Manager',
  'Operations — Operational Management',
  'Finance / HR',
  'Business Development',
  'Marketing / Brand',
  'Legal / Compliance',
  'Other',
];

export interface LocationGroup {
  state: string;
  locations: string[];
}

// Mirrors the client's final survey package (sweetspot-final-package_2):
// grouped by state, Retail vs Cultivation distinguished.
export const LOCATION_GROUPS: LocationGroup[] = [
  { state: 'Vermont', locations: ['Essex Junction, VT — Retail'] },
  { state: 'Rhode Island', locations: ['Exeter, RI — Retail', 'Warwick, RI — Cultivation'] },
  { state: 'Connecticut', locations: ['West Hartford, CT — Retail', 'Stamford, CT — Retail'] },
  {
    state: 'New Jersey',
    locations: [
      'River Edge, NJ — Retail',
      'Voorhees, NJ — Retail',
      'Mount Olive, NJ — Retail',
      'Hamilton, NJ — Retail',
    ],
  },
  { state: 'Maryland', locations: ['Maryland — Retail'] },
  { state: 'Other', locations: ['Remote', 'Other'] },
];

// Flat list kept for any consumer that just needs the full set of values.
export const LOCATIONS = LOCATION_GROUPS.flatMap(g => g.locations);

export const getSectionsForTrack = (track: SurveyTrack): SurveySection[] =>
  TRACK_ORDER[track].map(k => ALL[k]);

export const getAllQuestionsForTrack = (track: SurveyTrack): SurveyQuestion[] =>
  getSectionsForTrack(track).flatMap(s => s.questions);

export const getAllQuestionsAcrossTracks = (): SurveyQuestion[] => {
  const seen = new Set<string>();
  const out: SurveyQuestion[] = [];
  (['retail', 'cultivation', 'hq'] as SurveyTrack[]).forEach(t => {
    getAllQuestionsForTrack(t).forEach(q => {
      if (!seen.has(q.id)) {
        seen.add(q.id);
        out.push(q);
      }
    });
  });
  return out;
};
